import { t, Trans } from "@lingui/macro";
import { Check } from "@mui/icons-material";
import { Box, Step, StepIconProps, StepLabel, Stepper, Typography } from "@mui/material";
import { NavigationBack, ZPlayLoader, ZPlayNetworkService, ZPlayUploadLoader } from "commons";
import { ZipPlayStepIconRoot, ZipPlayStepperConnector } from "commons/commons";
import { SegregationType } from "commons/form-constants";
import { SegregationForm } from "components/backoffice/content";
import CreateAndEditSeriesDetailForm from "components/backoffice/series/create-and-edit-series-detail-form/create-and-edit-series-detail-form";
import { CreateSeriesRequest } from "models/request/create-series-request";
import { ErrorResponseApi, GetCollectionListResponse } from "models/response";
import { withSnackbar, WithSnackbarProps } from "notistack";
import React from "react";
import { ContentProducerService } from "services/content-producer-service";

export interface CreateSeriesState {
    isLoading: boolean
    isUploading: boolean
    uploadStageMessage: string
    currentUploadPercentage: number
    activeStep: number,
    thumbnailFile: File | null,
    thumbUploadUrl: any;
    currentUploadRequest: CreateSeriesRequest | null,
    collectionsList: GetCollectionListResponse
}

class CreateSeries extends React.Component<WithSnackbarProps, CreateSeriesState> {

    private EMPTY_COLLECTION_LIST: GetCollectionListResponse = {
        awardList: { list: [], size: 0 },
        countryList: { list: [], size: 0 },
        festivalList: { list: [], size: 0 },
        genreList: { list: [], size: 0 },
        languageList: { list: [], size: 0 },
        tagList: { list: [], size: 0 },
        contentIssuesList: { list: [], size: 0 },
        seriesList: { list: [], size: 0 }
    };

    constructor(props: any) {
        super(props);
        this.state = {
            isLoading: true,
            isUploading: false,
            uploadStageMessage: "",
            thumbUploadUrl: "",
            currentUploadPercentage: 0,
            activeStep: 0,
            thumbnailFile: null,
            currentUploadRequest: null,
            collectionsList: this.EMPTY_COLLECTION_LIST
        };
    }

    public componentDidMount() {
        ContentProducerService.getCollectionList(this.getCollectionListSuccessCallback, this.getCollectionListErrorCallback);
    };

    getCollectionListSuccessCallback = (data: GetCollectionListResponse) => {
        this.setState(() => ({
            collectionsList: data,
            isLoading: false
        }))
    }

    getCollectionListErrorCallback = (err: ErrorResponseApi) => {

        this.setState(() => ({
            isLoading: false
        }));

        this.props.enqueueSnackbar(t({
            id: "cs-error-get-collection-list-error",
            message: "Si è verificato un errore. Non è stato possibile recuperare i dati."
        }), {
            variant: "error",
        });
    }

    private checkDetailsStepValidity = (stepData: any): boolean => {

        let errorsArray: string[] = [];
        let warningArray: string[] = [];

        if (stepData.title.length === 0)
            errorsArray.push(t({
                id: "cs-form-validation-no-title",
                message: "È necessario inserire almeno un titolo"
            }));

        if (stepData.synopsis.length === 0)
            errorsArray.push(t({
                id: "cs-form-validation-no-synopsis",
                message: "È necessario inserire almeno una sinossi"
            }));

        if (!stepData.thumbnailFile)
            errorsArray.push(t({
                id: "cs-form-validation-no-thumbnail-file",
                message: "Non è stata caricata un'immagine di copertina"
            }));

        if (!stepData.language)
            errorsArray.push(t({
                id: "cs-form-validation-no-language",
                message: "Non è stato caricato nessuna lingua del film"
            }));

        if (stepData.director.length === 0)
            warningArray.push(t({
                id: "cs-form-validation-no-director",
                message: "Non è stato indicato nessun regista"
            }));

        if (warningArray.length > 0)
            this.props.enqueueSnackbar(warningArray.join(". "),
                { variant: "warning" });

        if (errorsArray.length > 0)
            this.props.enqueueSnackbar(errorsArray.join(". "),
                { variant: "error" });

        return errorsArray.length === 0;
    }


    private getDetailData = (stepData: any) => {

        if (!this.checkDetailsStepValidity(stepData))
            return;

        let currentUploadRequest: CreateSeriesRequest = {
            title: [],
            synopsis: [],
            language: "",
            director: "",
            productionYear: 0,
            segregationType: SegregationType.FREE,
            rentalAvailabilityDays: 0
        };

        currentUploadRequest.title = stepData.title;
        currentUploadRequest.synopsis = stepData.synopsis;
        currentUploadRequest.director = stepData.director;
        currentUploadRequest.language = stepData.language;
        currentUploadRequest.productionYear = stepData.productionYear;

        let thumbnailFile = stepData.thumbnailFile;

        currentUploadRequest.thumbnailData = {
            fileName: thumbnailFile.name,
            format: thumbnailFile.type.split("/")[1]
        };

        this.setState(() => ({
            currentUploadRequest: currentUploadRequest,
            activeStep: 1,
            thumbnailFile: thumbnailFile,
        }));

    };

    private stepIcon(props: StepIconProps) {

        const {
            active,
            completed,
            className
        } = props;

        return (<ZipPlayStepIconRoot ownerState={{ active }} className={className}>
            {completed ? (<Check className="QontoStepIcon-completedIcon" />) : (
                <div className="QontoStepIcon-circle" />)}
        </ZipPlayStepIconRoot>);
    }

    private backToStart = () => {
        this.setState(() => ({ activeStep: 0 }));
    }

    private getCsrrentStepContent = () => {

        switch (this.state.activeStep) {
            case 0:
                return (<CreateAndEditSeriesDetailForm
                    onDetailSubmit={this.getDetailData}
                    collectionsList={this.state.collectionsList}
                    series={null}
                />);
            case 1:
                return (<SegregationForm onSegregationSubmit={this.getSegregationData}
                    hasAvailabilityDays={true}
                    onSegregationBack={this.backToStart} />);
        }
    };

    private getSegregationData = (stepData: any) => {

        let currentUploadRequest = this.state.currentUploadRequest;

        if (!currentUploadRequest)
            return

        currentUploadRequest.segregationType = stepData.segregationType;
        currentUploadRequest.rentalAvailabilityDays = stepData.availabilityDays;

        if (currentUploadRequest.segregationType === SegregationType.PAY_PER_VIEW) {
            currentUploadRequest.payPerViewSegregationAdditionalInfos = {
                price: stepData.price,
                currency: stepData.currency
            };
        }

        this.setState(() => ({
            currentUploadRequest: currentUploadRequest,
        }));

        this.startCreation();
    };

    private startCreation = () => {

        this.setState(() => ({
            isUploading: true,
            uploadStageMessage: t({
                id: "cs-upload-pre-stage",
                message: "Preparazione al caricamento"
            })
        }));

        if (this.state.currentUploadRequest)
            ContentProducerService.createSeries(this.state.currentUploadRequest, this.startThumbUpload, this.errorCallback);
    }


    private startThumbUpload = (data: any) => {

        console.log(data)

        this.setState(() => ({
            thumbUploadUrl: data.s3PresignedThumbnailUrl,
            uploadStageMessage: t({
                id: "cu-upload-content-stage",
                message: "Caricamento dell'immagine di copertina in corso..."
            })
        }));

        ZPlayNetworkService.xhrMultipartUpload(this.state.thumbUploadUrl ?? "", this.state.thumbnailFile, this.progressCallback)
            .then(this.uploadEnd)
            .catch(this.errorCallback)
    };


    private uploadEnd = () => {

        this.setState(() => ({
            activeStep: 2,
            isUploading: false
        }));

    };

    private progressCallback = (percentage: number) => {
        this.setState(() => ({ currentUploadPercentage: percentage }))
    }


    private errorCallback = (err: any) => {
        console.log(err)
        this.props.enqueueSnackbar(t({
            id: "cs-error-submit-update-content-error",
            message: "Si è verificato un errore. Non è stato possibile creare la serie"
        }), {
            variant: "error",
        });
        this.setState(() => ({ isUploading: false }));
    };

    render() {
        return <Box sx={{ p: 5 }}>
            {this.state.isLoading && <ZPlayLoader />}
            <NavigationBack />
            <Box maxWidth={"1000px"} padding={"30px 10px 10px"} margin={"0 auto"}>
                {this.state.isUploading &&
                    <ZPlayUploadLoader progress={this.state.currentUploadPercentage}
                        message={this.state.uploadStageMessage} />}
                <Stepper activeStep={this.state.activeStep} connector={<ZipPlayStepperConnector />}
                    sx={{
                        pb: 5,
                    }} alternativeLabel>
                    <Step key={0}>
                        <StepLabel StepIconComponent={this.stepIcon}>
                            <Typography color={"#FFFFFF"}>
                                <Trans id="su-details">Dettagli</Trans>
                            </Typography>
                        </StepLabel>
                    </Step>
                    <Step>
                        <StepLabel StepIconComponent={this.stepIcon}>
                            <Typography color={"#FFFFFF"}>
                                <Trans id="su-segregation">Segregazione</Trans>
                            </Typography>
                        </StepLabel>
                    </Step>
                </Stepper>
                {!this.state.isLoading && <Box>
                    {this.getCsrrentStepContent()}
                </Box>}

            </Box>
        </Box>;
    }
}

export default withSnackbar(CreateSeries);